/**
 * How many files the sender currently has staged in the create composer (ui#1305).
 *
 * The composer's selection lives in React state and nowhere else, so any full-page navigation
 * destroys it. One decision outside the composer's subtree needs to know that BEFORE the navigation
 * starts — the login modal, whose provider buttons go straight to `signInWithRedirect` on mobile
 * (ui#830) — and this is the seam that tells it, without either side importing the other.
 *
 * It records the COUNT and never the payload, and that limit is the honest one rather than a
 * shortcut. A staged entry is a `File`: unrepresentable in `localStorage`/`sessionStorage` at all,
 * and copyable into IndexedDB only by duplicating the BYTES into origin storage — which for a
 * transfer app means writing gigabytes to origin storage to survive a sign-in, against a quota that
 * would refuse it. So nothing here pretends the selection can be restored; the count exists so the UI
 * can state truthfully what a navigation would cost and offer the sign-in that costs nothing.
 *
 * Module-scope state, in the shape `consent/consentStore.ts` already established for a fact two
 * unrelated subtrees share. No browser API is touched at all, so the module is inert during the SSG
 * prerender rather than merely guarded.
 */

let stagedFileCount = 0;

/** Files staged in the composer right now; 0 when the composer is empty or not mounted. */
export function composerFileCount(): number {
	return stagedFileCount;
}

/**
 * Records the composer's current file count. Negative, fractional and non-finite inputs collapse to
 * 0: this number is only ever read to decide whether there is something to lose, and a nonsense value
 * must read as "nothing", never as "something".
 */
export function setComposerFileCount(count: number): void {
	stagedFileCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
}
